import { PageTitle as SharedPageTitle } from '../../../components/layout/PageHeader';
import React from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { ConfirmModal } from '../../../components/ui/ConfirmModal';
import { Icon } from '../../../components/ui/Icon';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { queryKeys, useComplianceDocument } from '../../../hooks/useApi';
import { useTaskStatusQuery } from '../../../hooks/useTaskStatusQuery';
import type { ParsedTaskStatusResponse } from '../../../lib/schemas/task.schema';
import {
  isTaskTerminal,
  useTaskProcessingStore,
} from '../../../stores/taskProcessing.store';
import { apiService } from '../../../services/api';
import type {
  ApiError,
  ComplianceDocumentDetail,
  ComplianceEvent,
} from '../../../types/api';
import {
  formatDateShort,
  formatDateTime,
  formatFileSize,
} from '../../../utils/formatters';
import { ComplianceExpiryBadge } from './ComplianceExpiryBadge';
import { ComplianceEventsTable } from './ComplianceEventsTable';
import { ComplianceStatusBadge } from './ComplianceStatusBadge';
import {
  formatComplianceTypeLabel,
  getComplianceOcrStatusLabel,
} from '../lib/labels';

const PageContainer = styled.div`
  width: 100%;
  min-width: 0;
  padding: 0;
  margin: 0;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  margin-bottom: var(--space-xl);
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-md);
  align-items: center;
  justify-content: space-between;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const Title = styled(SharedPageTitle)``;

const Subtitle = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-wrap: wrap;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: var(--space-sm);
  flex-wrap: wrap;
`;

const SectionTitle = styled.h2`
  margin: 0 0 var(--space-md) 0;
  font-size: var(--font-size-lg);
  color: var(--text-primary);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--space-md);
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const InfoLabel = styled.span`
  font-size: var(--font-size-xs);
  letter-spacing: 0.04em;
  text-transform: none;
  color: var(--text-muted);
`;

const InfoValue = styled.span`
  color: var(--text-primary);
  font-size: var(--font-size-sm);
`;

const HelperText = styled.p`
  margin: var(--space-sm) 0 0 0;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
`;

const OcrHeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  align-items: center;
  justify-content: space-between;
`;

const OcrSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-md);
  margin-top: var(--space-md);
`;

const ErrorState = styled(Card)`
  text-align: center;
  padding: var(--space-3xl);
  color: var(--danger-600);
`;

const formatOptionalValue = (value: string | null | undefined) =>
  value && value.trim() ? value : '—';

const parseApiErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object') {
    const detail = (error as ApiError & { detail?: unknown }).detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (
      detail &&
      typeof detail === 'object' &&
      'message' in detail &&
      typeof (detail as { message?: string }).message === 'string'
    ) {
      return (detail as { message: string }).message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
};

const getLatestOcrTaskId = (events: ComplianceEvent[]): string | null => {
  for (const event of events) {
    if (
      event.event_type === 'ocr_started' ||
      event.event_type === 'ocr_failed' ||
      event.event_type === 'ocr_succeeded'
    ) {
      const metadata = event.event_metadata as { task_id?: string } | null;
      if (metadata?.task_id) {
        return metadata.task_id;
      }
    }
  }
  return null;
};

const readExtractedString = (
  fields: ComplianceDocumentDetail['extracted_fields'],
  key: string,
): string | null => {
  if (!fields || typeof fields !== 'object') {
    return null;
  }
  const value = (fields as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : null;
};

const readExtractedTags = (
  fields: ComplianceDocumentDetail['extracted_fields'],
): string[] | null => {
  if (!fields || typeof fields !== 'object') {
    return null;
  }
  const value = (fields as Record<string, unknown>).tags;
  return Array.isArray(value) ? value.map(String) : null;
};

const readTaskDocumentId = (
  details?: Record<string, unknown>,
): string | null => {
  if (!details) {
    return null;
  }
  const value = details.documentId;
  return typeof value === 'string' ? value : null;
};

const taskStatusLabels: Record<string, string> = {
  queued: 'En cola',
  processing: 'Procesando',
  retrying: 'Reintentando',
  success: 'Completado',
  failure: 'Fallido',
  canceled: 'Cancelado',
};

// Feature toggles (keep code, hide UI sections)
const SHOW_FILE_AND_NOTES_SECTION = false;
const SHOW_OCR_EXTRACTION_SECTION = false;
const SHOW_RENEW_BUTTON = false;

interface ComplianceDetailViewProps {
  documentId: string;
}

export const ComplianceDetailView: React.FC<ComplianceDetailViewProps> = ({
  documentId,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useComplianceDocument(documentId);

  const [confirmArchiveOpen, setConfirmArchiveOpen] = React.useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [ocrTaskId, setOcrTaskId] = React.useState<string | null>(null);
  const startTask = useTaskProcessingStore((state) => state.startTask);
  const updateTaskStatus = useTaskProcessingStore(
    (state) => state.updateTaskStatus,
  );
  const tasks = useTaskProcessingStore((state) => state.tasks);
  const mergeTaskStatusResponse = useTaskProcessingStore(
    (state) => state.mergeTaskStatusResponse,
  );
  const storedTaskId = React.useMemo(() => {
    for (const [taskId, task] of Object.entries(tasks)) {
      if (!task || task.subtype !== 'compliance') {
        continue;
      }
      const taskDocumentId = readTaskDocumentId(task.details);
      if (taskDocumentId === documentId) {
        return taskId;
      }
    }
    return null;
  }, [tasks, documentId]);
  const resolvedTaskId = ocrTaskId ?? storedTaskId;
  const existingTask = resolvedTaskId ? tasks[resolvedTaskId] : undefined;

  const extractedFields = data?.extracted_fields ?? null;
  const hasExtractedFields =
    extractedFields && Object.keys(extractedFields).length > 0;

  React.useEffect(() => {
    if (!data?.events) {
      return;
    }
    setOcrTaskId(getLatestOcrTaskId(data.events));
  }, [data?.events]);

  React.useEffect(() => {
    if (!ocrTaskId || existingTask) {
      return;
    }
    if (!data || data.ocr_status !== 'pending') {
      return;
    }

    const details: Record<string, unknown> = {
      documentId,
      ...(data.original_filename ? { filename: data.original_filename } : {}),
    };

    startTask({
      id: ocrTaskId,
      type: 'ocr',
      subtype: 'compliance',
      status: 'STARTED',
      normalizedStatus: 'processing',
      category: 'processing',
      resultId: null,
      error: null,
      details,
      timestamps: {
        startedAt: data.updated_at ?? new Date().toISOString(),
      },
    });
  }, [data, documentId, existingTask, ocrTaskId, startTask]);

  React.useEffect(() => {
    if (!resolvedTaskId) {
      return;
    }
    const task = tasks[resolvedTaskId];
    if (!task) {
      return;
    }
    const currentDocumentId = readTaskDocumentId(task.details);
    if (currentDocumentId === documentId) {
      return;
    }
    const nextDetails: Record<string, unknown> = {
      ...(task.details ?? {}),
      documentId,
      ...(data?.original_filename ? { filename: data.original_filename } : {}),
    };
    updateTaskStatus(resolvedTaskId, { details: nextDetails });
  }, [
    data?.original_filename,
    documentId,
    resolvedTaskId,
    tasks,
    updateTaskStatus,
  ]);

  const shouldPollOcr =
    Boolean(resolvedTaskId) && data?.ocr_status === 'pending';
  const handleTaskStatusSuccess = React.useCallback(
    (payload: ParsedTaskStatusResponse) => {
      mergeTaskStatusResponse(payload);
    },
    [mergeTaskStatusResponse],
  );
  const ocrTaskQuery = useTaskStatusQuery(resolvedTaskId, {
    enabled: shouldPollOcr,
    refetchInterval: 5000,
    staleTime: 0,
    onSuccess: handleTaskStatusSuccess,
  });
  const ocrStatusRef = React.useRef(ocrTaskQuery.normalizedStatus);

  React.useEffect(() => {
    const status = ocrTaskQuery.normalizedStatus;
    if (!status || status === ocrStatusRef.current) {
      return;
    }
    ocrStatusRef.current = status;

    if (
      data?.ocr_status === 'pending' &&
      (status === 'success' || status === 'failure' || status === 'canceled')
    ) {
      void refetch();
    }
  }, [data?.ocr_status, ocrTaskQuery.normalizedStatus, refetch]);

  const archiveMutation = useMutation({
    mutationFn: (docId: string) =>
      apiService.updateComplianceDocument(docId, { status: 'archived' }),
    onSuccess: () => {
      toast.success('Documento archivado.');
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.compliance.detail(documentId),
      });
      setConfirmArchiveOpen(false);
    },
    onError: (mutationError) => {
      const message =
        mutationError instanceof Error
          ? mutationError.message
          : 'No se pudo archivar el documento.';
      toast.error(message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => apiService.deleteComplianceDocument(docId),
    onSuccess: () => {
      toast.success('Documento eliminado.');
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.all });
      queryClient.removeQueries({
        queryKey: queryKeys.compliance.detail(documentId),
        exact: true,
      });
      navigate('/compliance');
    },
    onError: (mutationError) => {
      const message =
        mutationError instanceof Error
          ? mutationError.message
          : 'No se pudo eliminar el documento.';
      toast.error(message);
    },
  });

  const retryOcrMutation = useMutation({
    mutationFn: () => apiService.retryComplianceOcr(documentId),
    onSuccess: (response) => {
      toast.success('OCR encolado nuevamente.');
      setOcrTaskId(response.task_id);
      queryClient.invalidateQueries({
        queryKey: queryKeys.compliance.detail(documentId),
      });
    },
    onError: (mutationError) => {
      toast.error(
        parseApiErrorMessage(
          mutationError,
          'No se pudo reintentar la extracción.',
        ),
      );
    },
  });

  const handleDownload = React.useCallback(
    async (document: ComplianceDocumentDetail) => {
      if (!document.storage_key) {
        toast.error('El documento no tiene archivo adjunto.');
        return;
      }

      try {
        const response = await apiService.downloadComplianceDocument(
          document.id,
        );
        if (!response.presigned_get_url) {
          toast.error('No se pudo generar el enlace de descarga.');
          return;
        }
        window.open(
          response.presigned_get_url,
          '_blank',
          'noopener,noreferrer',
        );
        queryClient.invalidateQueries({
          queryKey: queryKeys.compliance.detail(document.id),
        });
      } catch (downloadError) {
        const message =
          downloadError instanceof Error
            ? downloadError.message
            : 'No se pudo descargar el documento.';
        toast.error(message);
      }
    },
    [queryClient],
  );

  const handleApplyOcr = React.useCallback(() => {
    navigate(`/compliance/${documentId}/edit`, {
      state: { prefillFromOcr: true },
    });
  }, [documentId, navigate]);

  if (isLoading) {
    return (
      <PageContainer>
        <Card>
          <LoadingSpinner message="Cargando documento..." />
        </Card>
      </PageContainer>
    );
  }

  if (error || !data) {
    return (
      <PageContainer>
        <ErrorState>
          <div style={{ fontSize: '2rem', marginBottom: 'var(--space-md)' }}>
            ⚠️
          </div>
          <p style={{ margin: 0, fontSize: '1rem' }}>
            No se pudo cargar el documento.
          </p>
          <ActionsRow style={{ justifyContent: 'center', marginTop: '1rem' }}>
            <Button variant="secondary" onClick={() => navigate('/compliance')}>
              Volver
            </Button>
            <Button variant="primary" onClick={() => refetch()}>
              Reintentar
            </Button>
          </ActionsRow>
        </ErrorState>
      </PageContainer>
    );
  }

  const isArchived = data.status === 'archived';
  const requiresUploadOnRenewal = data.requires_new_upload_on_renewal;
  const isProcessingFromTask =
    existingTask !== undefined && !isTaskTerminal(existingTask);
  const effectiveOcrStatus =
    data.ocr_status ?? (isProcessingFromTask ? 'pending' : null);
  const ocrStatusLabel = getComplianceOcrStatusLabel(effectiveOcrStatus);
  const normalizedTaskStatus =
    ocrTaskQuery.normalizedStatus ?? existingTask?.normalizedStatus;
  const taskStatusLabel = normalizedTaskStatus
    ? (taskStatusLabels[normalizedTaskStatus] ?? 'Procesando')
    : null;
  const extractedIssueDate = readExtractedString(extractedFields, 'issue_date');
  const extractedExpiryDate = readExtractedString(
    extractedFields,
    'expiry_date',
  );
  const extractedAuthority = readExtractedString(
    extractedFields,
    'issuing_authority',
  );
  const extractedJurisdiction = readExtractedString(
    extractedFields,
    'jurisdiction',
  );
  const extractedNotes = readExtractedString(extractedFields, 'notes');
  const extractedTags = readExtractedTags(extractedFields);
  const extractedConfidence =
    typeof extractedFields?.confidence === 'number'
      ? extractedFields.confidence
      : null;

  return (
    <PageContainer>
      <Header>
        <ActionsRow>
          <Button
            variant="tertiary"
            onClick={() =>
              navigate('/compliance', { state: { skipComplianceResume: true } })
            }
          >
            <Icon name="arrow-left" size="sm" aria-hidden />
            Volver a Cumplimiento
          </Button>
        </ActionsRow>

        <TitleRow>
          <TitleGroup>
            <Title>{formatComplianceTypeLabel(data.type)}</Title>
            <Subtitle>{formatOptionalValue(data.issuing_authority)}</Subtitle>
            <BadgeRow>
              <ComplianceStatusBadge status={data.status} />
              <ComplianceExpiryBadge
                expired={data.expired}
                expiringSoon={data.expiring_soon}
                daysToExpiry={data.days_to_expiry ?? null}
              />
            </BadgeRow>
          </TitleGroup>

          <ActionsRow>
            <Button
              variant="secondary"
              onClick={() => handleDownload(data)}
              disabled={!data.storage_key}
            >
              <Icon name="download" size="sm" aria-hidden />
              Descargar
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate(`/compliance/${documentId}/edit`)}
            >
              <Icon name="edit" size="sm" aria-hidden />
              Editar
            </Button>
            {SHOW_RENEW_BUTTON && (
              <Button
                variant="secondary"
                onClick={() => navigate(`/compliance/${documentId}/renew`)}
              >
                <Icon name="refresh" size="sm" aria-hidden />
                Renovar
              </Button>
            )}
            <Button
              variant="secondary"
              disabled={isArchived}
              onClick={() => setConfirmArchiveOpen(true)}
            >
              <Icon name="layers" size="sm" aria-hidden />
              Archivar
            </Button>
            <Button
              variant="secondary"
              onClick={() => setConfirmDeleteOpen(true)}
            >
              <Icon name="delete" size="sm" aria-hidden />
              Eliminar
            </Button>
          </ActionsRow>
        </TitleRow>
      </Header>

      <Card>
        <SectionTitle>Resumen</SectionTitle>
        <InfoGrid>
          <InfoItem>
            <InfoLabel>Tipo</InfoLabel>
            <InfoValue>{formatComplianceTypeLabel(data.type)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Autoridad emisora</InfoLabel>
            <InfoValue>{formatOptionalValue(data.issuing_authority)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Jurisdicción</InfoLabel>
            <InfoValue>{formatOptionalValue(data.jurisdiction)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Fecha de emisión</InfoLabel>
            <InfoValue>{formatDateShort(data.issue_date)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Fecha de vencimiento</InfoLabel>
            <InfoValue>{formatDateShort(data.expiry_date)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Días para vencer</InfoLabel>
            <InfoValue>
              {data.days_to_expiry !== null ? data.days_to_expiry : '—'}
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Próximo vencimiento</InfoLabel>
            <InfoValue>
              {data.next_due_date ? formatDateShort(data.next_due_date) : '—'}
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Estado OCR</InfoLabel>
            <InfoValue>
              {getComplianceOcrStatusLabel(data.ocr_status)}
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Última actualización</InfoLabel>
            <InfoValue>{formatDateTime(data.updated_at)}</InfoValue>
          </InfoItem>
        </InfoGrid>
        {requiresUploadOnRenewal && (
          <HelperText>
            Este documento requiere cargar un nuevo archivo al renovar.
          </HelperText>
        )}
      </Card>

      {SHOW_FILE_AND_NOTES_SECTION && (
        <Card style={{ marginTop: 'var(--space-lg)' }}>
          <SectionTitle>Archivo y notas</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Archivo</InfoLabel>
              <InfoValue>
                {formatOptionalValue(data.original_filename)}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Tamaño</InfoLabel>
              <InfoValue>
                {data.size_bytes !== null
                  ? formatFileSize(data.size_bytes)
                  : '—'}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Tipo MIME</InfoLabel>
              <InfoValue>{formatOptionalValue(data.mime_type)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Checksum</InfoLabel>
              <InfoValue>{formatOptionalValue(data.checksum)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Etiquetas</InfoLabel>
              <InfoValue>
                {data.tags && data.tags.length > 0 ? data.tags.join(', ') : '—'}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Notas</InfoLabel>
              <InfoValue>{formatOptionalValue(data.notes)}</InfoValue>
            </InfoItem>
          </InfoGrid>
        </Card>
      )}

      {SHOW_OCR_EXTRACTION_SECTION && (
        <Card style={{ marginTop: 'var(--space-lg)' }}>
          <OcrHeaderRow>
            <SectionTitle>Extracción OCR</SectionTitle>
            <ActionsRow>
              {hasExtractedFields && (
                <Button variant="secondary" size="sm" onClick={handleApplyOcr}>
                  <Icon name="edit" size="sm" aria-hidden />
                  Aplicar datos OCR
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => retryOcrMutation.mutate()}
                disabled={
                  retryOcrMutation.isPending ||
                  data.ocr_status === 'pending' ||
                  !data.storage_key
                }
                title={!data.storage_key ? 'Documento sin archivo' : undefined}
              >
                <Icon name="refresh" size="sm" aria-hidden />
                Reintentar OCR
              </Button>
            </ActionsRow>
          </OcrHeaderRow>
          <HelperText role="status" aria-live="polite">
            Estado actual: {ocrStatusLabel}
            {taskStatusLabel ? ` · ${taskStatusLabel}` : ''}
          </HelperText>
          {ocrTaskQuery.data?.error && (
            <HelperText style={{ color: 'var(--danger-600)' }}>
              {ocrTaskQuery.data.error}
            </HelperText>
          )}
          {hasExtractedFields ? (
            <OcrSummaryGrid>
              <InfoItem>
                <InfoLabel>Fecha de emisión (OCR)</InfoLabel>
                <InfoValue>
                  {extractedIssueDate
                    ? formatDateShort(extractedIssueDate)
                    : '—'}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Fecha de vencimiento (OCR)</InfoLabel>
                <InfoValue>
                  {extractedExpiryDate
                    ? formatDateShort(extractedExpiryDate)
                    : '—'}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Autoridad (OCR)</InfoLabel>
                <InfoValue>{formatOptionalValue(extractedAuthority)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Jurisdicción (OCR)</InfoLabel>
                <InfoValue>
                  {formatOptionalValue(extractedJurisdiction)}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Etiquetas (OCR)</InfoLabel>
                <InfoValue>
                  {extractedTags && extractedTags.length > 0
                    ? extractedTags.join(', ')
                    : '—'}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Confianza (OCR)</InfoLabel>
                <InfoValue>
                  {extractedConfidence !== null
                    ? `${(extractedConfidence * 100).toFixed(1)}%`
                    : '—'}
                </InfoValue>
              </InfoItem>
              <InfoItem style={{ gridColumn: '1 / -1' }}>
                <InfoLabel>Notas (OCR)</InfoLabel>
                <InfoValue>{formatOptionalValue(extractedNotes)}</InfoValue>
              </InfoItem>
            </OcrSummaryGrid>
          ) : (
            <HelperText>
              {effectiveOcrStatus === 'pending'
                ? 'Estamos procesando el documento. Estos datos aparecerán al finalizar.'
                : data.ocr_status === 'failed'
                  ? 'No se pudo extraer información. Puedes reintentar el OCR.'
                  : 'Todavía no hay datos extraídos para este documento.'}
            </HelperText>
          )}
        </Card>
      )}

      <Card style={{ marginTop: 'var(--space-lg)' }}>
        <SectionTitle>Historial</SectionTitle>
        <ComplianceEventsTable events={data.events} />
      </Card>

      <ConfirmModal
        isOpen={confirmArchiveOpen}
        onClose={() => setConfirmArchiveOpen(false)}
        onConfirm={() => archiveMutation.mutate(documentId)}
        title="Archivar documento"
        message="El documento quedará archivado y no enviará más recordatorios."
        confirmText="Archivar"
        cancelText="Cancelar"
        isLoading={archiveMutation.isPending}
        variant="warning"
      />

      <ConfirmModal
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={() => deleteMutation.mutate(documentId)}
        title="Eliminar documento"
        message="Esta acción eliminará el documento de cumplimiento."
        confirmText="Eliminar"
        cancelText="Cancelar"
        isLoading={deleteMutation.isPending}
        variant="danger"
      />
    </PageContainer>
  );
};

ComplianceDetailView.displayName = 'ComplianceDetailView';
